/**
 * Purchasing — Frontend Entry Point
 *
 * Generated from ISG App Template.
 * Uses @isg/ui-kit for shared CSS, components, and services.
 */

// --- Styles (from @isg/ui-kit) ---
import '@isg/ui-kit/css/tokens.css';
import '@isg/ui-kit/css/reset.css';
import '@isg/ui-kit/css/components.css';
import '@isg/ui-kit/css/layout.css';

// App-specific styles, built entirely on ui-kit tokens.
import './styles/worklist.css';
import './styles/part-forecast.css';
/* 🔴 **THIS IMPORT WAS MISSING FOR TWO WEEKS AND NOBODY NOTICED** (found
   2026-09-02). `reports.css` was added on 2026-08-20 and edited by three
   sessions since — 297 lines, 69 selectors covering the buyers page, the
   inventory forecast, parts, routing and supplier items — and **not one rule
   has ever shipped**, because nothing imported it. Vite bundles what is
   reachable from the entry point; an orphaned stylesheet is silently absent
   from `dist/assets/*.css` and no build step complains.
   ⚠️ It fails in the reassuring direction: the pages still RENDER, just
   unstyled in the places these rules were meant to reach, so it reads as "we
   never styled that" rather than as a broken build. Almost every selector is
   page-prefixed (`by-`, `rp-`, `rt-`, `if-`, `pt-`, `si-`) and currently
   matches nothing, so turning it on adds styling rather than changing any.
   ⚠️ `test_every_page_stylesheet_is_imported` now fails on an orphan. */
import './styles/reports.css';

// --- Services & Utils (from @isg/ui-kit barrel) ---
// Everything shared comes from the barrel — routing, auth, navigation,
// branding, the env banner, charts, toasts, modals. Do not hand-roll any of
// these; if a component is missing, add it to ui-kit so every app gains it.
import { api, router, store, auth, checkEnvBanner } from '@isg/ui-kit';

import { wireCopyButtons } from './utils/part.js';
import { renderNavigation, renderMobileHeader, createSidebarOverlay } from '@isg/ui-kit';
import { applyBranding, loadBranding } from '@isg/ui-kit';

// Floating AI assistant — how-to for everyone, agentic for admins.
// LOCAL MODEL ONLY: purchasing content is restricted (see CLAUDE.md DO #5).
import { renderAIAssistant } from './components/ai-assistant-widget.js';
import {
  mountCompanySwitcher, patchApiForCompanyOverride,
} from './company-switcher.js';

// ⚠️ BEFORE any page fetches. The patch appends `?company_id=` to every
// request while an override is active, so a call made before it lands would
// silently return the caller's own scope instead of the one on screen.
patchApiForCompanyOverride();

/* SSO: capture the handoff from the central intranet broker.
 *
 * MUST stay at module top level, before init(). The broker returns the user to
 * /auth/sso-callback?handoff=…, and this exchanges that single-use JWT for auth
 * cookies and navigates onward.
 *
 * Omitting it does NOT produce an error — it produces a BLANK WHITE PAGE, which
 * is why this line kept getting missed. ui-kit's checkSession() deliberately
 * returns a never-resolving promise on that path so it cannot race the exchange,
 * so `await auth.checkSession()` in init() simply hangs forever. A hang is not a
 * throw, so init().catch()'s "Failed to load" fallback never fires either.
 *
 * Added to the template 2026-07-18 after isg-bi became the 15th app to hit this.
 * Every other user-facing app had already patched it individually.
 */
auth.captureSSOHandoff();

// --- Lazy-loaded pages ---
const pages = {
  dashboard: () => import('./pages/dashboard.js'),
  worklist:  () => import('./pages/worklist.js'),
  deadOrders: () => import('./pages/dead-orders.js'),
  // 11a — shelf life. 39.3% of uk stock VALUE sits in dated lots and nothing
  // in this app knew until 2026-08-17.
  shelfLife: () => import('./pages/shelf-life.js'),
  // 41 — Melksham. 57.7% of Rayfast UK's stock is forty miles away and QX
  // calls it the same warehouse; the warehouse currently wings the trips.
  offsite:   () => import('./pages/offsite.js'),
  // The record behind every worklist: what was actioned, what the overnight
  // check made of each claim, and the follow-through notes (2026-10-30).
  actionLog: () => import('./pages/action-log.js'),
  reports:   () => import('./pages/reports.js'),
  issues:    () => import('./pages/issues.js'),
  documents: () => import('./pages/documents.js'),
  hotTopics: () => import('./pages/hot-topics.js'),
  // 10c — every part, whether anyone sells it or not.
  parts:     () => import('./pages/parts.js'),
  // 10e — the UoM Review surface, moved from isg-pricing 2026-08-12.
  uomReview: () => import('./pages/uom-review.js'),
  // 15 — the supplier directory. 740 of 919 uk codes we trade with carry no
  // name anywhere in the estate, and this is where purchasing supplies them.
  suppliers: () => import('./pages/suppliers.js'),
  // 18 — where a part should land, and the arithmetic that says so.
  routing: () => import('./pages/routing.js'),
  // 16 — who is relevant to a part. The document names six range owners;
  // `PO Input By` names eighteen codes that actually raise the orders.
  buyers: () => import('./pages/buyers.js'),
  // 38 — the NWC projection. Two scenarios, because one number would be read
  // as a prediction and used in a bonus calculation.
  inventoryForecast: () => import('./pages/inventory-forecast.js'),
  // Each company's sales by product group — `app/models/demand_forecast.py`.
  demandForecast: () => import('./pages/demand-forecast.js'),
  // One part's statistical outlook — `app/services/part_outlook.py`.
  partForecast: () => import('./pages/part-forecast.js'),
  // 40. Supplier Items — every listed price for a part beside what we pay.
  supplierItems: () => import('./pages/supplier-items.js'),
  // 40b. The library behind Supplier Items — and where its validity periods
  // are recorded. Admin, because it WRITES what every price comparison rests on.
  priceLists: () => import('./pages/price-lists.js'),
  login:     () => import('./pages/login.js'),
};

// ── Navigation config ────────────────────────────────────────────────
// Sections group nav items. Use section: null for top-level items.
const NAV_CONFIG = [
  {
    section: null,
    items: [
      { path: '/', label: 'Dashboard', icon: 'layout-dashboard' },
      // The worklist is the app. It sits directly under Dashboard rather than
      // inside a section because burying the one surface that names an action
      // would defeat the point.
      // ⚠️ `clipboard-list` is verified present in ui-kit's BUILTIN_ICONS.
      // An unknown name does not error — it renders an EMPTY nav item.
      { path: '/worklist', label: 'Worklist', icon: 'clipboard-list' },
      // Sits beside the worklist because it IS one — 572 SKUs / £2.37M of
      // order book to review and close, oldest due 2001. It was computed from
      // the day the demand book shipped and rendered nowhere.
      // ⚠️ `clock` verified present in ui-kit BUILTIN_ICONS (navigation.js).
      // An unknown name does not error — it renders an EMPTY nav item.
      { path: '/dead-orders', label: 'Dead orders', icon: 'clock' },
      // Sits beside Dead orders because it is the same KIND of work — a list
      // of things quietly going bad that no other surface was raising. The
      // demand book was marking positions COVERED on stock that will not be
      // shippable when the order is due.
      // ⚠️ `calendar-check` verified present in ui-kit BUILTIN_ICONS. An
      // unknown name does not error — it renders an EMPTY nav item.
      { path: '/shelf-life', label: 'Shelf life', icon: 'calendar-check' },
      // Sits with the other worklists because it IS one, and because its
      // audience is the warehouse rather than a buyer — they currently decide
      // when to drive to Melksham by feel.
      // ⚠️ `truck` verified present in ui-kit BUILTIN_ICONS. An unknown name
      // does not error — it renders an EMPTY nav item.
      // 🔴 **CONDITIONAL, and `relevantOnly` is a TIDINESS control** — the
      // same status as `adminOnly` below. The link is hidden from companies
      // whose stock is nowhere near a far site (Rayfast USA, Astro, LJR); the
      // route and the endpoints stay reachable and gate themselves.
      //
      // ⚠️ Relevance is DERIVED from where a company's demand actually sits,
      // asked of the server — never a hardcoded company list here. Measured
      // 2026-09-03: eight uk divisions have demand at RF and all eight have
      // parts at Melksham, so a literal list would have been wrong on five of
      // them from day one.
      { path: '/offsite', label: 'Offsite', icon: 'truck', relevantOnly: true },
      // The department's ask (2026-10-30): the historic record of actions and
      // notes, browsable — with what the overnight check verified. Sits with
      // the worklists because it is their memory, not a report.
      // ⚠️ `history` verified present in ui-kit BUILTIN_ICONS
      // (navigation.js:239). An unknown name does not error — it renders an
      // EMPTY nav item.
      { path: '/action-log', label: 'Action log', icon: 'history' },
      // 🔴 **PROBLEM PARTS IS OFF THE NAV** (Dominika, 2026-09-02: *"Problem
      // parts - this is not needed as we have Hot Topics already"*). Measured
      // before agreeing: **zero** records have ever been created in
      // `purchasing_issues`, `purchasing_issue_parts` or `issue_comments`
      // since launch, against 3 Hot Topics. The department is right — they
      // adopted one surface and not the other.
      //
      // ⚠️ **The ROUTE, the page, the blueprint and the four tables all
      // STAY.** Nothing is deleted, because nothing needs to be: removing a
      // nav entry is one line to reverse and loses no data, and the tables
      // are empty so there is nothing to migrate if this is revisited. What
      // it stops is a dead surface diluting the pages people do use.
      //
      // ⚠️ It was renamed from "Issues" only twelve days ago (2026-08-21) to
      // make it distinct from Hot Topics. The rename was the right fix for
      // the wrong problem — the surface was not confusing, it was unwanted.
      // { path: '/issues', label: 'Problem Parts', icon: 'clipboard-check' },
      // The shared document component, as every other app in the estate has.
      // The page and BOTH blueprints were wired on 2026-08-05 and the route
      // was simply never added — so a complete, working surface sat
      // unreachable. ⚠️ `folder` verified present in ui-kit BUILTIN_ICONS.
      // ⚠️ `megaphone` verified present in ui-kit BUILTIN_ICONS.
      { path: '/hot-topics', label: 'Hot Topics', icon: 'megaphone' },
      // ⚠️ `bar-chart` verified present in ui-kit BUILTIN_ICONS — an unknown
      // name renders an EMPTY nav item rather than erroring.
      // 10c. Sits ABOVE Reports because it answers a question people
      // arrive with ("what do we know about this part?") rather than one they
      // go looking for. ⚠️ `package` verified present in ui-kit
      // BUILTIN_ICONS — an unknown name renders an EMPTY nav item.
      { path: '/parts', label: 'Parts', icon: 'package' },
      // 10e. Sits beside Parts because it is the same subject — the item
      // master — and it is the one screen here that WRITES it.
      // ⚠️ `ruler` verified present in ui-kit BUILTIN_ICONS; an unknown name
      // renders an EMPTY nav item rather than erroring.
      { path: '/uom-review', label: 'UoM Review', icon: 'ruler' },
      // 40. Beside UoM Review because they are the same conversation from two
      // ends: this shows what a supplier lists a part at, that one settles
      // the unit the listing is in. ⚠️ `tag` verified present in ui-kit
      // BUILTIN_ICONS — an unknown name renders an EMPTY nav item.
      { path: '/supplier-items', label: 'Supplier Items', icon: 'tag' },
      // 15. Beside Parts and UoM Review because it is the same kind of
      // surface — a master purchasing OWNS rather than reads.
      // ⚠️ `building` verified present in ui-kit BUILTIN_ICONS; an unknown
      // name renders an EMPTY nav item rather than erroring.
      // 18. ⚠️ `git-branch` verified present in ui-kit BUILTIN_ICONS; an
      // unknown name renders an EMPTY nav item rather than erroring.
      { path: '/routing', label: 'Routing', icon: 'git-branch' },
      // 16. Beside Suppliers because it is the same kind of surface — a
      // directory purchasing OWNS rather than reads.
      // ⚠️ `users` verified present in ui-kit BUILTIN_ICONS; an unknown name
      // renders an EMPTY nav item rather than erroring.
      // 38. ⚠️ `bar-chart` verified present in ui-kit BUILTIN_ICONS.
      { path: '/inventory-forecast', label: 'Inventory forecast',
        icon: 'presentation-chart-line' },
      // 2026-09-24 — each company's sales by product group, 12 months out.
      // ⚠️ `trending-up` verified present in ui-kit BUILTIN_ICONS.
      { path: '/demand-forecast', label: 'Demand forecast',
        icon: 'trending-up' },
      // 2026-09-28 — one part: demand, projected stock, when to buy.
      // ⚠️ `activity` verified present in ui-kit BUILTIN_ICONS.
      { path: '/part-forecast', label: 'Part forecast',
        icon: 'activity' },
    ],
  },
  // ── Admin (2026-08-21) ────────────────────────────────────────────
  //
  // 🔴 The daily surfaces above are what a buyer opens; these four are what
  // an administrator MAINTAINS — the document library, the supplier master,
  // the buyer directory and the report pack. Mixed into one flat list they
  // made the nav long enough that the worklist stopped being obviously the
  // point of the app (DO #0).
  //
  // ⚠️ **`adminOnly` here is a TIDINESS control, not a security boundary.**
  // ui-kit filters the nav on `auth.isAppAdmin('purchasing')`, which is a
  // claim in the browser — anyone can still type the URL. Every one of these
  // endpoints is gated SERVER-side on its own permission, which is where the
  // decision actually holds (DO #11: an API that returned the data to
  // someone unentitled has already leaked it).
  {
    section: 'Admin',
    items: [
      // ⚠️ `folder`, `building`, `users` and `bar-chart` are all verified
      // present in ui-kit BUILTIN_ICONS — an unknown name does not error, it
      // renders an EMPTY nav item.
      { path: '/suppliers', label: 'Suppliers', icon: 'building',
        adminOnly: true },
      { path: '/buyers', label: 'Buyers', icon: 'users', adminOnly: true },
      { path: '/documents', label: 'Documents', icon: 'folder',
        adminOnly: true },
      // ⚠️ `file-text` verified present in ui-kit BUILTIN_ICONS — an unknown
      // name renders an EMPTY nav item.
      { path: '/price-lists', label: 'Supplier Files', icon: 'file-text',
        adminOnly: true },
      // ⚠️ **Reports is the one to watch.** `can_view_reports` is held by
      // EVERY role including `viewer`, deliberately, so hiding it from the
      // nav for non-admins removes a surface buyers use today. The server
      // still serves them — the link is simply no longer offered. Say so if
      // anyone asks where Reports went, and move it back out if they do.
      { path: '/reports', label: 'Reports', icon: 'bar-chart',
        adminOnly: true },
    ],
  },
];

/**
 * The nav, minus anything this company has no use for.
 *
 * ⚠️ **`relevantOnly` is filtered HERE rather than in ui-kit** because the
 * question is purchasing's own — "does this company keep stock at a far site"
 * — and ui-kit has no way to know it. It is the same class of control as
 * `adminOnly`: it decides what is OFFERED, never what is permitted, and every
 * endpoint behind it gates itself server-side (DO #11).
 *
 * ⚠️ **`null` means we could not ask, and it KEEPS the item.** Hiding a
 * surface because a lookup failed is a removal with no error, no symptom and
 * nothing for the reader to report. Same direction as DO #28: show something
 * that could have been hidden, never hide something that should have shown.
 */
function navFor(relevant) {
  if (relevant !== false) return NAV_CONFIG;
  return NAV_CONFIG.map((group) => ({
    ...group,
    items: group.items.filter((i) => !i.relevantOnly),
  })).filter((group) => group.items.length);
}

/**
 * Does the offsite surface apply to whoever is signed in?
 *
 * ⚠️ Asked ONCE at boot, which is sound because switching company reloads the
 * page (`company-switcher.js`). If that ever stops being true, this has to be
 * re-asked on the switch or the nav will describe the previous company.
 */
async function loadOffsiteRelevance() {
  try {
    const d = await api.get('/offsite/relevance');
    return d && typeof d.relevant === 'boolean' ? d.relevant : null;
  } catch {
    return null;                       // could not ask — keep the link
  }
}

let offsiteRelevant = null;

// ── Render a page module into #main-content ──────────────────────────
async function renderPage(loader, params = {}) {
  const main = document.getElementById('main-content');
  if (!main) return;

  main.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:50vh">
      <div class="spinner"></div>
    </div>
  `;

  try {
    const module = await loader();
    main.innerHTML = '';
    /* 🔴 Bound ONCE on the document, not per page or per row. These tables
       re-render on every filter, sort and page turn, so a per-button listener
       would leak and — worse — would not exist on the rows drawn by the NEXT
       render. `wireCopyButtons` is idempotent. */
    wireCopyButtons(document);
    await module.render(main, params);
  } catch (err) {
    console.error('Page render error:', err);
    main.innerHTML = `
      <div class="main-content-inner">
        <div class="empty-state">
          <p style="color: var(--red-600); font-weight: 500;">Something went wrong</p>
          <p>${err.message || 'Failed to load page.'}</p>
          <button class="btn btn-primary mt-4" onclick="location.reload()">Reload</button>
        </div>
      </div>
    `;
  }
}

// ── App shell ────────────────────────────────────────────────────────
function renderAppShell() {
  const app = document.getElementById('app');
  app.innerHTML = '';

  // Accent stripe at top
  const stripe = document.createElement('div');
  stripe.className = 'accent-stripe';
  app.appendChild(stripe);

  const layout = document.createElement('div');
  layout.className = 'app-layout';

  const mobileHeader = renderMobileHeader({ appTitle: 'Purchasing' });
  const overlay = createSidebarOverlay();

  const sidebar = renderNavigation({
    navConfig: navFor(offsiteRelevant),
    // ⚠️ Required for `adminOnly` to mean anything: without it ui-kit falls
    // back to the ESTATE-wide admin flag, so a purchasing administrator would
    // not see their own admin section and an estate admin with no purchasing
    // role would.
    appSlug: 'purchasing',
    appTitle: 'Purchasing',
    fallbackInitial: 'P',
  });

  const main = document.createElement('main');
  main.className = 'main-content';
  main.id = 'main-content';

  layout.appendChild(mobileHeader);
  layout.appendChild(overlay);
  layout.appendChild(sidebar);
  layout.appendChild(main);
  app.appendChild(layout);

}

// ── Init ─────────────────────────────────────────────────────────────
async function init() {
  // Pre-load branding so sidebar logo is ready
  await loadBranding().catch(() => {});
  const hasSession = await auth.checkSession();

  // Define routes
  router
    .on('/', (params) => renderPage(pages.dashboard, params))
    .on('/worklist', (params) => renderPage(pages.worklist, params))
    .on('/dead-orders', (params) => renderPage(pages.deadOrders, params))
    .on('/shelf-life', (params) => renderPage(pages.shelfLife, params))
    .on('/offsite', (params) => renderPage(pages.offsite, params))
    .on('/action-log', (params) => renderPage(pages.actionLog, params))
    .on('/parts', (params) => renderPage(pages.parts, params))
    .on('/uom-review', (params) => renderPage(pages.uomReview, params))
    .on('/supplier-items', (params) => renderPage(pages.supplierItems, params))
    .on('/price-lists', (params) => renderPage(pages.priceLists, params))
    .on('/suppliers', (params) => renderPage(pages.suppliers, params))
    .on('/routing', (params) => renderPage(pages.routing, params))
    .on('/buyers', (params) => renderPage(pages.buyers, params))
    .on('/inventory-forecast', (params) => renderPage(pages.inventoryForecast, params))
    .on('/demand-forecast', (params) => renderPage(pages.demandForecast, params))
    .on('/part-forecast', (params) => renderPage(pages.partForecast, params))
    .on('/reports', (params) => renderPage(pages.reports, params))
    .on('/issues', (params) => renderPage(pages.issues, params))
    .on('/documents', (params) => renderPage(pages.documents, params))
    .on('/hot-topics', (params) => renderPage(pages.hotTopics, params))
    .on('/login', (params) => renderPage(pages.login, params));

  // Auth guard — redirects unauthenticated users to login
  router.before((path) => {
    if (path === '/login') {
      /* An AUTHENTICATED user must never be left on the login page. Returning
       * true unconditionally here (as this did until 2026-07-18) renders the
       * login FORM inside the app shell — the app appears signed in and signed
       * out simultaneously, and only a manual reload clears it.
       *
       * Not an edge case: the SSO broker returns the user to whatever `next` it
       * was carrying, and a failed exchange rewrites that to /login?sso_error=…,
       * so the first SUCCESSFUL login after any SSO hiccup lands here. Back
       * button and bookmarks reach it too. */
      if (store.get('isAuthenticated')) {
        router.navigate('/');
        return false;
      }
      return true;
    }
    if (!store.get('isAuthenticated')) {
      router.navigate('/login');
      return false;
    }
    return true;
  });

  // Apply branding after every route change
  router.after(() => {
    setTimeout(() => applyBranding(), 50);
  });

  if (!hasSession) {
    // No session — show login
    const app = document.getElementById('app');
    app.innerHTML = '<main id="main-content" role="main"></main>';
    const module = await pages.login();
    /* AWAIT the render. `render()` is async — it fetches /auth/config before it
     * writes any markup — so without this, applyBranding() below runs against an
     * EMPTY container: `.login-hero-logo` doesn't exist yet, the selector misses,
     * and the page keeps the placeholder LETTER instead of the ISG logo. Silent:
     * nothing errors, the branding call just finds nothing to brand. */
    await module.render(document.getElementById('main-content'));
    if (window.location.pathname !== '/login') {
      window.history.replaceState(null, '', '/login');
    }
    applyBranding();
  } else {
    // Authenticated — render app shell and resolve current route.
    // ⚠️ BEFORE the shell: the nav is built once and the answer decides
    // whether one of its items exists. A late answer would either flash the
    // link or require re-rendering the sidebar under the reader.
    offsiteRelevant = await loadOffsiteRelevance();
    renderAppShell();
    // After the shell, so the sidebar nav exists to dock into.
    mountCompanySwitcher();
    checkEnvBanner();          // beta/test banner — a no-op on live
    await router.resolve();
    applyBranding();

    // Mount the AI assistant once — it floats over every page and owns its
    // own container's innerHTML.
    if (!document.getElementById('ai-assistant-mount')) {
      const aiMount = document.createElement('div');
      aiMount.id = 'ai-assistant-mount';
      document.body.appendChild(aiMount);
      renderAIAssistant(aiMount);
    }
  }
}

// Boot
init().catch((err) => {
  console.error('Failed to initialize app:', err);
  document.getElementById('app').innerHTML = `
    <div style="display:flex;align-items:center;justify-content:center;min-height:100vh;flex-direction:column;gap:1rem;">
      <p>Failed to load the application. Please refresh.</p>
      <button class="btn btn-primary" onclick="location.reload()">Reload</button>
    </div>
  `;
});
